/* ================================================================
   Record Builder - ConvertRecord / AdjustData equivalent.
   Converts solved layouts into DrawRecord[] and RenderNode[]
   with identity caching: unchanged SolvedLayerLayout references
   produce identical DrawRecord and RenderNode[] references so
   React.memo downstream can skip re-rendering.
   ================================================================ */

import type {
  ProjectModel, DrawRecord, PositionedElement, RenderNode, Vec3,
} from '../model/types';
import { SIDE_ID_LIST } from '../model/types';
import type { SolvedSideLayout, SolvedLayerLayout, SolvedElement } from '../solve';

// --------------- Identity caches ---------------

interface LayerRecordCache {
  record: DrawRecord;
  nodes: RenderNode[];
  material: number;
  color: string;
}

const recordCache = new WeakMap<SolvedLayerLayout, LayerRecordCache>();

export function clearRecordCache(): void {
  // WeakMap clears itself via GC; no explicit clear needed.
}

// --------------- DrawRecord builder ---------------

export function buildDrawRecords(
  project: ProjectModel,
  solvedLayouts: SolvedSideLayout[],
): DrawRecord[] {
  const records: DrawRecord[] = [];

  for (const sideLayout of solvedLayouts) {
    const sideIdx = sideLayout.sideIndex;
    for (const layerLayout of sideLayout.layers) {
      const layerIdx = layerLayout.layerIndex;
      const layer = project.sides[sideIdx]?.layers[layerIdx];
      const mat = layer?.styleSetting?.material ?? 1;
      const matColor = layer?.styleSetting?.materialcolor ?? '#c8a96e';

      const cached = recordCache.get(layerLayout);
      if (cached && cached.material === mat && cached.color === matColor) {
        records.push(cached.record);
        continue;
      }

      const record = buildLayerRecord(layerLayout, sideIdx, layerIdx, mat, matColor);
      const nodes = buildLayerNodes(record);
      recordCache.set(layerLayout, { record, nodes, material: mat, color: matColor });
      records.push(record);
    }
  }

  return records;
}

function buildLayerRecord(
  layerLayout: SolvedLayerLayout,
  sideIdx: number,
  layerIdx: number,
  mat: number,
  matColor: string,
): DrawRecord {
  const boards: PositionedElement[] = [];
  const frames: PositionedElement[][] = [];
  const horBraces: PositionedElement[][] = [];

  let elemIdx = 0;
  for (const b of layerLayout.boards) {
    boards.push(solvedToPositioned(b, mat, matColor, `${sideIdx}-${layerIdx}-${elemIdx++}`));
  }
  for (const frameGroup of layerLayout.frames) {
    const group: PositionedElement[] = [];
    for (const f of frameGroup) {
      group.push(solvedToPositioned(f, mat, matColor, `${sideIdx}-${layerIdx}-${elemIdx++}`));
    }
    frames.push(group);
  }
  for (const braceGroup of layerLayout.horBraces) {
    const group: PositionedElement[] = [];
    for (const br of braceGroup) {
      group.push(solvedToPositioned(br, mat, matColor, `${sideIdx}-${layerIdx}-${elemIdx++}`));
    }
    horBraces.push(group);
  }

  return {
    recordNo: sideIdx * 10 + layerIdx,
    sideId: SIDE_ID_LIST[sideIdx],
    sideIndex: sideIdx,
    layerIndex: layerIdx,
    boards,
    frames,
    horBraces,
    position: calculateCenterPosition(boards, frames, horBraces),
  };
}

function solvedToPositioned(
  elem: SolvedElement,
  material: number,
  materialcolor: string,
  meshId: string,
): PositionedElement {
  return {
    width: elem.geoW,
    thickness: elem.geoH,
    length: elem.geoD,
    position: { ...elem.position },
    material,
    materialcolor,
    meshId,
  };
}

function calculateCenterPosition(
  boards: PositionedElement[],
  frames: PositionedElement[][],
  horBraces: PositionedElement[][],
): Vec3 {
  let sumX = 0, sumY = 0, sumZ = 0, n = 0;
  for (const b of boards) { sumX += b.position.x; sumY += b.position.y; sumZ += b.position.z; n++; }
  for (const group of frames) for (const f of group) { sumX += f.position.x; sumY += f.position.y; sumZ += f.position.z; n++; }
  for (const group of horBraces) for (const br of group) { sumX += br.position.x; sumY += br.position.y; sumZ += br.position.z; n++; }
  if (n === 0) return { x: 0, y: 0, z: 0 };
  return { x: sumX / n, y: sumY / n, z: sumZ / n };
}

// --------------- RenderNode builder (identity-cached) ---------------

export function buildRenderNodes(drawRecords: DrawRecord[]): RenderNode[] {
  // When the caller used buildDrawRecords above, each record is tied to a
  // SolvedLayerLayout via recordCache. Look those up and reuse node arrays.
  const nodes: RenderNode[] = [];
  for (const record of drawRecords) {
    const cached = findCachedNodesForRecord(record);
    if (cached) {
      for (const n of cached) nodes.push(n);
    } else {
      const fresh = buildLayerNodes(record);
      for (const n of fresh) nodes.push(n);
    }
  }
  return nodes;
}

function findCachedNodesForRecord(record: DrawRecord): RenderNode[] | null {
  // Walk recordCache entries — small number (bounded by number of layers)
  // Using WeakMap iteration isn't possible; instead we piggy-back on
  // Object.is by storing a back-ref. We attach a hidden symbol to
  // DrawRecord at construction time in buildLayerRecord.
  const back = (record as any).__nodes as RenderNode[] | undefined;
  return back ?? null;
}

function buildLayerNodes(record: DrawRecord): RenderNode[] {
  const list: RenderNode[] = [];
  let elemIdx = 0;

  for (const board of record.boards) {
    list.push({
      meshId: board.meshId,
      geometry: { width: board.width, height: board.thickness, depth: board.length },
      position: board.position,
      material: board.material,
      materialcolor: board.materialcolor,
      sideId: record.sideId,
      sideIndex: record.sideIndex,
      layerIndex: record.layerIndex,
      elementIndex: elemIdx++,
      elementType: 'board',
    });
  }
  for (const group of record.frames) {
    for (const frame of group) {
      list.push({
        meshId: frame.meshId,
        geometry: { width: frame.width, height: frame.thickness, depth: frame.length },
        position: frame.position,
        material: frame.material,
        materialcolor: frame.materialcolor,
        sideId: record.sideId,
        sideIndex: record.sideIndex,
        layerIndex: record.layerIndex,
        elementIndex: elemIdx++,
        elementType: 'frame',
      });
    }
  }
  for (const group of record.horBraces) {
    for (const brace of group) {
      list.push({
        meshId: brace.meshId,
        geometry: { width: brace.width, height: brace.thickness, depth: brace.length },
        position: brace.position,
        material: brace.material,
        materialcolor: brace.materialcolor,
        sideId: record.sideId,
        sideIndex: record.sideIndex,
        layerIndex: record.layerIndex,
        elementIndex: elemIdx++,
        elementType: 'horBrace',
      });
    }
  }

  (record as any).__nodes = list;
  return list;
}

export { solveProject } from '../solve';
